import React, { useEffect, useState } from "react";
import GoalList from "../goals/GoalList";
import GoalForm from "../goals/GoalForm";
import Modal from "../common/Modal";
import ConfirmDialog from "../common/ConfirmDialog";
import GoalDetail from "../goals/GoalDetail";
import { useTier } from "../../hooks/useTier";
import { useUpsell } from "../paywall/UpsellProvider";
import { useLanguage } from "../../i18n/LanguageContext";

import {
  fetchGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  recommendGoals,
} from "../../api/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const GoalsPage = () => {
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showGoalDetailModal, setShowGoalDetailModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [recommendedGoals, setRecommendedGoals] = useState([]);
  const [goalPendingDelete, setGoalPendingDelete] = useState(null);
  const { isPremium } = useTier();
  const upsell = useUpsell();
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  const {
    data: goals,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["goals"],
    queryFn: fetchGoals,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: recommendationData,
    isLoading: isLoadingRecommendations,
    isError: isRecommendationError,
    error: recommendationError,
  } = useQuery({
    queryKey: ["goalRecommendations"],
    queryFn: async () => {
      const recommendations = await recommendGoals();
      return recommendations.goals || recommendations;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (recommendationData) {
      setRecommendedGoals(recommendationData);
    }
  }, [recommendationData]);

  const visibleRecommendedGoals = isPremium
    ? recommendedGoals
    : recommendedGoals.slice(0, 3);

  const createGoalMutation = useMutation({
    mutationFn: createGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      closeModal();
    },
    onError: (error) => {
      console.error("Error creating goal:", error);
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: ({ goalId, goalData }) => updateGoal(goalId, goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      closeModal();
    },
    onError: (error) => {
      console.error("Error updating goal:", error);
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      setGoalPendingDelete(null);
      closeGoalDetailModal();
    },
    onError: (error) => {
      console.error("Error deleting goal:", error);
    },
  });

  const handleSaveGoal = async (goalToSave) => {
    const goalData = {
      title: goalToSave.title,
      type: goalToSave.type,
      targetDate: goalToSave.targetDate,
      category: goalToSave.category,
      priority: goalToSave.priority,
      description: goalToSave.description,
      ...(goalToSave.id && { progress: goalToSave.progress }),
    };

    if (goalToSave.id) {
      updateGoalMutation.mutate({ goalId: goalToSave.id, goalData });
    } else {
      createGoalMutation.mutate(goalData);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    setGoalPendingDelete(goalId);
  };

  const confirmDeleteGoal = () => {
    if (!goalPendingDelete) {
      return;
    }
    deleteGoalMutation.mutate(goalPendingDelete);
  };

  const handleAcceptRecommendation = (rec) => {
    if (!isPremium) {
      upsell?.open(t("upsell.goalAcceptReason"));
      return;
    }
    openAddGoalModal(rec);
  };

  const handleDeclineRecommendation = (index) => {
    if (!isPremium) {
      upsell?.open(t("upsell.goalManageReason"));
      return;
    }
    setRecommendedGoals((prev) => prev.filter((_, i) => i !== index));
  };

  const openAddGoalModal = (goal = null) => {
    setEditingGoal(goal);
    setShowGoalModal(true);
  };

  const openEditGoalModal = (goal) => {
    setEditingGoal(goal);
    setShowGoalModal(true);
  };

  const closeModal = () => {
    setShowGoalModal(false);
    setEditingGoal(null);
  };

  const openGoalDetailModal = (goal) => {
    setSelectedGoal(goal);
    setShowGoalDetailModal(true);
  };

  const closeGoalDetailModal = () => {
    setSelectedGoal(null);
    setShowGoalDetailModal(false);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("goals.title")}</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => openAddGoalModal()}
            className="rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
          >
            {t("goals.new")}
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold">{t("goals.suggestions")}</h3>
            {!isPremium && (
              <span className="rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shrink-0">
                {t("common.premium")}
              </span>
            )}
          </div>
          {isRecommendationError && (
            <p className="text-red-500 italic mb-4">
              {t("goals.failedRecommendations")} {recommendationError?.message}
            </p>
          )}
          {isLoadingRecommendations ? (
            <p className="text-sm text-gray-500">{t("goals.loadingSuggestions")}</p>
          ) : visibleRecommendedGoals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleRecommendedGoals.map((rec, index) => (
                <div
                  key={index}
                  className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                >
                  <h4 className="font-bold">{rec.title}</h4>
                  <p className="mt-1 flex-1 text-sm text-gray-600">{rec.description}</p>
                  <div className="mt-4 flex gap-2 pt-2">
                    <button
                      onClick={() => handleAcceptRecommendation(rec)}
                      className="rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-purple-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
                    >
                      {t("goals.accept")}
                    </button>
                    <button
                      onClick={() => handleDeclineRecommendation(index)}
                      className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
                    >
                      {t("goals.decline")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("goals.noRecommendations")}</p>
          )}
        </div>
      </div>

      {isLoading && (
        <p className="text-center text-gray-500 italic">{t("goals.loading")}</p>
      )}
      {isError && (
        <p className="text-center text-red-500 italic">
          {t("common.error")}: {error?.message}
        </p>
      )}
      {!isLoading && !isError && goals && (
        <GoalList
          goals={goals}
          onEditGoal={(goal) => {
            openEditGoalModal(goal);
            closeGoalDetailModal();
          }}
          onDeleteGoal={(goalId) => {
            handleDeleteGoal(goalId);
          }}
          onGoalClick={openGoalDetailModal}
        />
      )}

      {showGoalModal && (
        <Modal
          title={editingGoal ? t("goals.edit") : t("goals.create")}
          onClose={closeModal}
        >
          <GoalForm
            onClose={closeModal}
            onSave={handleSaveGoal}
            editGoal={editingGoal}
          />
        </Modal>
      )}

      {showGoalDetailModal && selectedGoal && (
        <GoalDetail
          goal={selectedGoal}
          onClose={closeGoalDetailModal}
          onEdit={(goal) => {
            openEditGoalModal(goal);
            closeGoalDetailModal();
          }}
          onDelete={(goalId) => {
            handleDeleteGoal(goalId);
          }}
        />
      )}

      {goalPendingDelete && (
        <ConfirmDialog
          title={t("goals.deleteTitle")}
          message={t("goals.deleteMessage")}
          confirmLabel={t("goals.deleteConfirm")}
          isConfirming={deleteGoalMutation.isPending}
          onCancel={() => setGoalPendingDelete(null)}
          onConfirm={confirmDeleteGoal}
        />
      )}
    </div>
  );
};

export default GoalsPage;
